import {FileModel} from "./models";

//
//basic definition of how records are defined
//on flow screens
//
export interface BaseRecordJSON {
  'Id_sys': number,
  'Uuid_sys': string,
  'object_api_name': string,
  'lookup_display_value': string
}

export class BaseRecordModel {
  Id_sys: number;
  Uuid_sys: string;
  objectApiName: string;
  displayValue: string;

  constructor(
    Id_sys: number,
    Uuid_sys: string,
    objectApiName: string,
    displayValue: string
  ) {

    this.Id_sys = Id_sys;
    this.Uuid_sys = Uuid_sys;
    this.objectApiName = objectApiName;
    this.displayValue = displayValue;
  }
}

export class BaseRecordFactory {

  fromJSON(json: BaseRecordJSON): BaseRecordModel {
    return new BaseRecordModel(
      json.Id_sys,
      json.Uuid_sys,
      json.object_api_name,
      json.lookup_display_value
    );
  }

  toJSON(model: BaseRecordModel): BaseRecordJSON {
    return {
      Id_sys: model.Id_sys,
      Uuid_sys: model.Uuid_sys,
      object_api_name: model.objectApiName,
      lookup_display_value: model.displayValue
    }
  }
}

/************************************************/
/************************************************/
/************************************************/


//base screen element always has a rank and a label/component name
export interface BaseScreenElementJSON {
  'api_name': string,
  'rank': number,
  'component': string
}

export class BaseScreenElementModel {
  apiName: string;
  rank: number;
  component: string;

  constructor(
    apiName: string,
    rank: number,
    component: string
  ) {
    this.apiName = apiName;
    this.rank = rank;
    this.component = component;
  }
}

export class BaseScreenElementFactory {

  fromJSON(json: BaseScreenElementJSON): BaseScreenElementModel {
    return new BaseScreenElementModel(
      json.api_name,
      json.rank,
      json.component
    );
  }

  toJSON(model: BaseScreenElementModel): BaseScreenElementJSON {
    return {
      api_name: model.apiName,
      rank: model.rank,
      component: model.component
    };
  }
}

/************************************************/
/************************************************/
/************************************************/

export interface TitleScreenElementJSON extends BaseScreenElementJSON {
  'value': string;
}
export class TitleScreenElementModel extends BaseScreenElementModel {

  value: string;

  constructor(
    apiName: string,
    rank: number,
    component: string,
    value: string
  ) {
    super(apiName, rank, component);
    this.value = value
  }

  validate(): boolean {
    return true
  }
}

export class TitleScreenElementFactory {
  fromJSON(json: TitleScreenElementJSON): TitleScreenElementModel {
    return new TitleScreenElementModel(
      json.api_name,
      json.rank,
      json.component,
      json.value
    );
  }
  toJSON(model: TitleScreenElementModel): TitleScreenElementJSON {
    return {
      api_name: model.apiName,
      rank: model.rank,
      component: model.component,
      value: model.value
    };
  }
}


/************************************************/
export interface BaseSelectItemJSON {
  'key': string,
  'value': string
}

export class BaseSelectItemModel {
  key: string;
  value: string;

  constructor(
    key: string,
    value: string
  ) {

    this.key = key;
    this.value = value;
  }
}

export class BaseSelectItemFactory {

  fromJSON(json: BaseSelectItemJSON): BaseSelectItemModel {
    return new BaseSelectItemModel(
      json.key,
      json.value
    );
  }

  toJSON(model: BaseSelectItemModel): BaseSelectItemJSON {
    return {
      key: model.key,
      value: model.value
    }
  }
}


export interface SelectItemScreenElementJSON extends BaseScreenElementJSON {
  'items': BaseSelectItemJSON[];
  'selected_item': BaseSelectItemJSON | null;
}
export class SelectItemScreenElementModel extends BaseScreenElementModel {

  items: BaseSelectItemModel[];
  selectedItem: BaseSelectItemModel | null;

  constructor(
    apiName: string,
    rank: number,
    component: string,
    items: BaseSelectItemModel[],
    selectedItem: BaseSelectItemModel | null
  ) {
    super(apiName, rank, component);
    this.items = items;
    this.selectedItem = selectedItem;
  }

  validate(): boolean {
    return this.selectedItem !== null;
  }

}
export class SelectItemScreenElementFactory {
  fromJSON(json: SelectItemScreenElementJSON): SelectItemScreenElementModel {
    const thisFactory = new BaseSelectItemFactory();
    const items = json.items.map((thisItem: BaseSelectItemJSON) => thisFactory.fromJSON(thisItem))

    let selectedItem = null;
    if(json.selected_item) {
      selectedItem = thisFactory.fromJSON(json.selected_item);
    }

    return new SelectItemScreenElementModel(
      json.api_name,
      json.rank,
      json.component,
      items,
      selectedItem
    );
  }
  toJSON(model: SelectItemScreenElementModel): SelectItemScreenElementJSON {
    const thisFactory = new BaseSelectItemFactory();
    const items = model.items.map((thisItem: BaseSelectItemModel) => thisFactory.toJSON(thisItem))
    let selectedItem = null;
    if(model.selectedItem) selectedItem = thisFactory.toJSON(model.selectedItem);
    return {
      api_name: model.apiName,
      rank: model.rank,
      component: model.component,
      items: items,
      selected_item: selectedItem
    };
  }
}

/************************************************/

export interface SelectRecordScreenElementJSON extends BaseScreenElementJSON {
  'records': BaseRecordJSON[];
  'selected_record': BaseRecordJSON | null;
}
export class SelectRecordScreenElementModel extends BaseScreenElementModel {

  records: BaseRecordModel[];
  selectedRecord: BaseRecordModel | null;

  constructor(
    apiName: string,
    rank: number,
    component: string,
    records: BaseRecordModel[],
    selectedRecord: BaseRecordModel | null
  ) {
    super(apiName, rank, component);
    this.records = records;
    this.selectedRecord = selectedRecord;
  }

  validate(): boolean {
    return this.selectedRecord !== null;
  }

}
export class SelectRecordScreenElementFactory {
  fromJSON(json: SelectRecordScreenElementJSON): SelectRecordScreenElementModel {
    const thisFactory = new BaseRecordFactory();
    const records = json.records.map((thisRecord: BaseRecordJSON) => thisFactory.fromJSON(thisRecord))

    let selectedRecord = null;
    if(json.selected_record) {
      selectedRecord = thisFactory.fromJSON(json.selected_record);
    }

    return new SelectRecordScreenElementModel(
      json.api_name,
      json.rank,
      json.component,
      records,
      selectedRecord
    );
  }
  toJSON(model: SelectRecordScreenElementModel): SelectRecordScreenElementJSON {
    const thisFactory = new BaseRecordFactory();
    const records = model.records.map((thisRecord: BaseRecordModel) => thisFactory.toJSON(thisRecord))
    let selectedRecord = null;
    if(model.selectedRecord) selectedRecord = thisFactory.toJSON(model.selectedRecord);
    return {
      api_name: model.apiName,
      rank: model.rank,
      component: model.component,
      records: records,
      selected_record: selectedRecord
    };
  }
}

/************************************************/

export type InputFieldElementType = string | number | boolean | null

export interface InputFieldScreenElementJSON extends BaseScreenElementJSON {
  "type": string,
  "label": string,
  "max_length": number,
  "visible_lines": number,
  "allowed_extensions": string[],
  'value': InputFieldElementType
}
export class InputFieldScreenElementModel extends BaseScreenElementModel {

  type: string;
  label: string;
  maxLength: number;
  visibleLines : number;
  allowedExtensions: string[];
  value: InputFieldElementType;

  constructor(
    apiName: string,
    rank: number,
    component: string,
    label: string,
    type: string,
    maxLength: number,
    visibleLines: number,
    allowedExtensions: string[],
    value: InputFieldElementType
  ) {
    super(apiName, rank, component);
    this.label = label;
    this.type = type;
    this.maxLength = maxLength;
    this.visibleLines = visibleLines;
    this.allowedExtensions = allowedExtensions;
    this.value = value;
  }

  validate(): boolean {
    return this.value !== null;
  }

}
export class InputFieldScreenElementFactory {
  fromJSON(json: InputFieldScreenElementJSON): InputFieldScreenElementModel {
    return new InputFieldScreenElementModel(
      json.api_name,
      json.rank,
      json.component,
      json.label,
      json.type,
      json.max_length,
      json.visible_lines,
      json.allowed_extensions,
      json.value
    );
  }
  toJSON(model: InputFieldScreenElementModel): InputFieldScreenElementJSON {
    return {
      api_name: model.apiName,
      rank: model.rank,
      component: model.component,
      label: model.label,
      type: model.type,
      max_length: model.maxLength,
      visible_lines: model.visibleLines,
      allowed_extensions: model.allowedExtensions,
      value: model.value
    };
  }
}

/************************************************/
/************************************************/
/************************************************/

export interface OutputFieldScreenElementJSON extends BaseScreenElementJSON {
  'value': string,
  'label': string,
  'type': string

}
export class OutputFieldScreenElementModel extends BaseScreenElementModel {

  value: string;
  label: string;
  type: string;

  constructor(
    apiName: string,
    rank: number,
    component: string,
    value: string,
    label: string,
    type: string
  ) {
    super(apiName, rank, component);
    this.value = value
    this.label = label
    this.type = type
  }

  validate(): boolean {
    return true
  }
}

export class OutputFieldScreenElementFactory {
  fromJSON(json: OutputFieldScreenElementJSON): OutputFieldScreenElementModel {
    return new OutputFieldScreenElementModel(
      json.api_name,
      json.rank,
      json.component,
      json.value,
      json.label,
      json.type
    );
  }
  toJSON(model: OutputFieldScreenElementModel): OutputFieldScreenElementJSON {
    return {
      api_name: model.apiName,
      rank: model.rank,
      component: model.component,
      value: model.value,
      label: model.value,
      type: model.type
    };
  }
}

/************************************************/

export interface Jinja2BodyScreenElementJSON extends BaseScreenElementJSON {
  'body': string;
}
export class Jinja2BodyScreenElementModel extends BaseScreenElementModel {

  body: string;

  constructor(
    apiName: string,
    rank: number,
    component: string,
    body: string
  ) {
    super(apiName, rank, component);
    this.body = body
  }

  validate(): boolean {
    return true
  }
}

export class Jinja2BodyScreenElementFactory {
  fromJSON(json: Jinja2BodyScreenElementJSON): Jinja2BodyScreenElementModel {
    return new Jinja2BodyScreenElementModel(
      json.api_name,
      json.rank,
      json.component,
      json.body
    );
  }
  toJSON(model: Jinja2BodyScreenElementModel): Jinja2BodyScreenElementJSON {
    return {
      api_name: model.apiName,
      rank: model.rank,
      component: model.component,
      body: model.body
    };
  }
}

/************************************************/
/************************************************/
/************************************************/

export type ScreenElementTypeJSON = TitleScreenElementJSON |
                                    SelectItemScreenElementJSON |
                                    SelectRecordScreenElementJSON |
                                    InputFieldScreenElementJSON |
                                    OutputFieldScreenElementJSON |
                                    Jinja2BodyScreenElementJSON;
export type ScreenElementTypeModel = TitleScreenElementModel |
                                     SelectItemScreenElementModel |
                                     SelectRecordScreenElementModel |
                                     InputFieldScreenElementModel |
                                     OutputFieldScreenElementModel |
                                     Jinja2BodyScreenElementModel;

export class ScreenElementFactory {
  fromJSON(json: ScreenElementTypeJSON): ScreenElementTypeModel {
    switch(json.component){
      case '<Title>':
        return new TitleScreenElementFactory().fromJSON(json as TitleScreenElementJSON)
      case '<SelectItem>':
        return new SelectItemScreenElementFactory().fromJSON(json as SelectItemScreenElementJSON)
      case '<SelectRecord>':
        return new SelectRecordScreenElementFactory().fromJSON(json as SelectRecordScreenElementJSON)
      case '<InputField>':
        return new InputFieldScreenElementFactory().fromJSON(json as InputFieldScreenElementJSON)
      case '<OutputField>':
        return new OutputFieldScreenElementFactory().fromJSON(json as OutputFieldScreenElementJSON)
      case '<Jinja2Body>':
        return new Jinja2BodyScreenElementFactory().fromJSON(json as Jinja2BodyScreenElementJSON)
      default:
        throw new Error(`Unsupported component type: ${JSON.stringify(json)}`)
    }
  }
  toJSON(model: ScreenElementTypeModel): ScreenElementTypeJSON {
    switch(model.component){
      case '<Title>':
        return new TitleScreenElementFactory().toJSON(model as TitleScreenElementModel)
      case '<SelectItem>':
        return new SelectItemScreenElementFactory().toJSON(model as SelectItemScreenElementModel)
      case '<SelectRecord>':
        return new SelectRecordScreenElementFactory().toJSON(model as SelectRecordScreenElementModel)
      case '<InputField>':
        return new InputFieldScreenElementFactory().toJSON(model as InputFieldScreenElementModel)
      case '<OutputField>':
        return new OutputFieldScreenElementFactory().toJSON(model as OutputFieldScreenElementModel)
      case '<Jinja2Body>':
        return new Jinja2BodyScreenElementFactory().toJSON(model as Jinja2BodyScreenElementModel)
      default:
        throw new Error(`Unsupported component type: ${model.component}`)
    }
  }
}

/************************************************/
/************************************************/
/************************************************/
//FlowScreen represents the UIConfig_sys and data
//from a flow screen in the backend

export interface FlowScreenJSON {
  api_name: string,
  title: string,
  elements: ScreenElementTypeJSON[]
}

export class FlowScreenModel {
  apiName: string;
  title: string;
  elements: ScreenElementTypeModel[];
  files: Record<string,FileModel>;

  constructor(
    apiName: string,
    title: string,
    elements: ScreenElementTypeModel[],
    files: Record<string,FileModel>
  ) {
    this.apiName = apiName;
    this.title = title;
    this.elements = elements;
    this.files = files;
  }
}
export class FlowScreenFactory {
  fromJSON(json: FlowScreenJSON): FlowScreenModel {
    const thisFactory = new ScreenElementFactory();
    const elements = json.elements.sort((thisElementA: ScreenElementTypeJSON, thisElementB: ScreenElementTypeJSON) => {
                                              if(thisElementA.rank >= thisElementB.rank) return 1;
                                              return -1;
                                            })
                                   .map((thisElement: ScreenElementTypeJSON) => thisFactory.fromJSON(thisElement));
    const files: Record<string,FileModel> = {};

    return new FlowScreenModel(
      json.api_name,
      json.title,
      elements,
      files
    );
  }
  toJSON(model: FlowScreenModel): FlowScreenJSON {
    const thisFactory = new ScreenElementFactory();
    const elements = model.elements.map((thisElement: ScreenElementTypeModel) => thisFactory.toJSON(thisElement));

    return {
      api_name: model.apiName,
      title: model.title,
      elements: elements
    };
  }
  getFILES(model: FlowScreenModel): FileModel[] {
    return Object.values(model.files)
  }
}

/************************************************/
/************************************************/
/************************************************/


export interface FlowRunJSON {
  Id_sys: string;
  Uuid_sys: string;
}

export class FlowRunModel {
  Id_sys: string;
  Uuid_sys: string;

  constructor(
    Id_sys: string,
    Uuid_sys: string
  ) {

    this.Id_sys = Id_sys;
    this.Uuid_sys = Uuid_sys;
  }
}


export class FlowRunFactory {
  fromJSON(json: FlowRunJSON): FlowRunModel {
    return new FlowRunModel(
      json.Id_sys,
      json.Uuid_sys
    );
  }
  toJSON(model: FlowRunModel): FlowRunJSON {
    return {
      Id_sys: model.Id_sys,
      Uuid_sys: model.Uuid_sys
    };
  }
}

/************************************************/
/************************************************/
/************************************************/


export interface FlowSuccessJSON {
  status: string;
  message: string;
}

export class FlowSuccessModel {
  status: string;
  message: string;

  constructor(
    status: string,
    message: string
  ) {

    this.status = status;
    this.message = message;
  }
}


export class FlowSuccessFactory {
  fromJSON(json: FlowSuccessJSON): FlowSuccessModel {
    return new FlowSuccessModel(
      json.status,
      json.message
    );
  }
  toJSON(model: FlowSuccessModel): FlowSuccessJSON {
    return {
      status: model.status,
      message: model.message
    };
  }
}
